/**
 * Fechas del perfil.
 *
 * En la API conviven dos marcos horarios distintos y mezclarlos pinta horas
 * que no son:
 *
 *  - Las fechas de sesión y de evento (`sessions.start_date`,
 *    `events.start_date`) son `timestamp without time zone` y guardan la hora
 *    de PARED de Madrid. Un concierto a las 20:30 guarda 20:30 y hay que
 *    pintar 20:30, esté el usuario donde esté. Por eso se parsean a trozos y
 *    no con `new Date(str)`: el navegador las interpretaría en su propia zona
 *    y a alguien de Canarias le saldrían las 19:30.
 *
 *  - `orders.created_at` sí va en UTC, porque la app tiene `app.timezone` a
 *    UTC. Esa sí hay que convertirla, y siempre a Madrid, que es la hora en
 *    la que el usuario recuerda haber comprado.
 */

const ZONA = 'Europe/Madrid';

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const DIAS_CORTOS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  /** El mismo instante como Date local, solo para comparar y ordenar. */
  date: Date;
}

/**
 * Descompone "2026-10-04 20:30:00" (o con la T de ISO) en sus trozos, sin
 * pasar en ningún momento por una zona horaria.
 */
export function parseWallClock(value: string | null | undefined): WallClock | null {
  if (!value) {
    return null;
  }

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!match) {
    return null;
  }

  const [, year, month, day, hour, minute] = match.map(Number) as any;

  return {
    year, month, day, hour, minute,
    date: new Date(year, month - 1, day, hour, minute)
  };
}

function dosDigitos(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/** "Sábado 4 de octubre · 20:30 h" */
export function eventDateLong(value: string | null | undefined): string {
  const w = parseWallClock(value);
  if (!w) {
    return '';
  }

  const dia = DIAS[w.date.getDay()];
  const nombreDia = dia.charAt(0).toUpperCase() + dia.slice(1);

  return `${nombreDia} ${w.day} de ${MESES[w.month - 1]} · ${dosDigitos(w.hour)}:${dosDigitos(w.minute)} h`;
}

/** "Sáb 4 oct · 20:30 h" */
export function eventDateShort(value: string | null | undefined): string {
  const w = parseWallClock(value);
  if (!w) {
    return '';
  }

  const dia = DIAS_CORTOS[w.date.getDay()];
  const nombreDia = dia.charAt(0).toUpperCase() + dia.slice(1);

  return `${nombreDia} ${w.day} ${MESES_CORTOS[w.month - 1]} · ${dosDigitos(w.hour)}:${dosDigitos(w.minute)} h`;
}

/**
 * La fecha de compra, que llega en UTC, en hora de Madrid: "12 sep 2026".
 * Con `conHora`, "12 sep 2026 · 18:42 h". En palabras y no en números, igual
 * que las fechas de los eventos que van al lado ("Sáb 10 oct").
 */
export function purchaseDate(value: string | null | undefined, conHora: boolean = false): string {
  if (!value) {
    return '';
  }

  // El backend manda "2026-09-12 16:42:00" sin marca de zona; sin la Z el
  // navegador lo tomaría por hora local en vez de por UTC.
  const iso = String(value).includes('T') ? String(value) : String(value).replace(' ', 'T');
  const date = new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`);

  if (isNaN(date.getTime())) {
    return '';
  }

  // Las partes en Madrid y el mes de MESES_CORTOS, para que se escriba igual
  // que en las fechas de los eventos ("sept." de Intl no es lo mismo).
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA,
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  }).formatToParts(date);
  const valor = (tipo: string) => Number(partes.find(p => p.type === tipo)?.value || 0);

  const fecha = `${valor('day')} ${MESES_CORTOS[valor('month') - 1]} ${valor('year')}`;

  if (!conHora) {
    return fecha;
  }

  const hora = new Intl.DateTimeFormat('es-ES', {
    timeZone: ZONA,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);

  return `${fecha} · ${hora} h`;
}

/**
 * Cuántos días faltan para el evento, contando por días de calendario y no
 * por horas: algo que es mañana a las 09:00 "falta 1 día", aunque queden 15
 * horas. Devuelve null si ya pasó.
 */
export function daysUntil(value: string | null | undefined): number | null {
  const w = parseWallClock(value);
  if (!w) {
    return null;
  }

  const hoy = new Date();
  const inicioHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  const inicioEvento = new Date(w.year, w.month - 1, w.day);

  const dias = Math.round((inicioEvento.getTime() - inicioHoy.getTime()) / 86400000);

  return dias < 0 ? null : dias;
}

/** "Hoy", "Mañana", "Faltan 7 días". */
export function countdownLabel(value: string | null | undefined): string {
  const dias = daysUntil(value);

  if (dias === null) {
    return '';
  }
  if (dias === 0) {
    return 'Hoy';
  }
  if (dias === 1) {
    return 'Mañana';
  }

  return `Faltan ${dias} días`;
}

/**
 * Horas que un evento sigue contando como "próximo" después de empezar: quien
 * llega tarde y abre sus entradas en la cola tiene que encontrarlas en
 * Próximas y en el banner, no en Pasadas. El back aplica el mismo margen
 * (UserOrderListService::MARGEN_EN_CURSO); si se cambia uno, se cambia el otro.
 */
export const MARGEN_EN_CURSO_HORAS = 4;

/** Ahora mismo, en hora de pared de Madrid, sea cual sea la zona del navegador. */
function ahoraEnMadrid(): Date {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(new Date());

  const valor = (tipo: string) => Number(partes.find(p => p.type === tipo)?.value || 0);

  return new Date(
    valor('year'), valor('month') - 1, valor('day'),
    valor('hour') % 24, valor('minute')
  );
}

/**
 * ¿Este evento ya se ha celebrado? Se compara la hora de pared del evento
 * con la hora de pared de Madrid, no con la del navegador, para que la
 * pestaña en la que cae una entrada sea la misma que dice la API. Y con el
 * margen de los que están en curso: no pasa a "pasado" al empezar.
 */
export function isPast(value: string | null | undefined): boolean {
  const w = parseWallClock(value);
  if (!w) {
    return false;
  }

  const fin = w.date.getTime() + MARGEN_EN_CURSO_HORAS * 3600000;
  return fin < ahoraEnMadrid().getTime();
}

/** Ya ha empezado pero sigue dentro del margen: está pasando ahora. */
export function isOngoing(value: string | null | undefined): boolean {
  const w = parseWallClock(value);
  if (!w) {
    return false;
  }

  return w.date.getTime() <= ahoraEnMadrid().getTime() && !isPast(value);
}
